<template>
  <div>
    <v-row align="center" class="mx-auto">
      <v-col cols="12" class="mb-4">
        <v-alert dense outlined type="info" color="blue" class="pa-2">
          Selecione o arquivo da base para upload. Formatos aceitos:
          <b>XLSX, XLSB, CSV, TSV, TXT, ZIP, PARQUET, DBF</b>
          .
          <br />
          Para arquivos
          <b>CSV / TSV / TXT</b>
          , informe encoding e delimitador.
          <br />
          Para
          <b>ZIP</b>
          , informe também o nome do arquivo interno a ser processado.
        </v-alert>
      </v-col>
    </v-row>

    <v-row align="center" class="mx-auto">
      <v-col cols="12" class="pl-2">
        <v-file-input
          v-model="arquivo"
          label="Arquivo da base"
          placeholder="Selecione um arquivo"
          outlined
          accept=".xlsx,.xlsb,.csv,.tsv,.txt,.zip,.parquet,.dbf"
          :show-size="!!arquivo && !fakeFile"
          truncate-length="30"
          :loading="uploading"
          loader-height="4"
          :success="uploadSucesso"
          :error-messages="fileError"
          @change="onFileChange"
          @click:clear="clearArquivo"
        />
      </v-col>
    </v-row>

    <v-row v-if="arquivo" align="center" class="mx-auto">
      <v-col cols="12" class="pl-2">
        <v-text-field
          v-model="nomeBase"
          label="Nome da base"
          outlined
          maxlength="200"
          :readonly="nomeBaseTravado"
          :hint="
            nomeBaseTravado
              ? 'Para alterar o nome, selecione o arquivo novamente e refaça o upload'
              : 'Usado nas abas e nos rótulos de coluna do arquivo de resultado'
          "
          persistent-hint
        />
      </v-col>
    </v-row>

    <v-row v-if="precisaEncoding || isZip" align="center" class="mx-auto">
      <v-col v-if="precisaEncoding" cols="12" sm="4">
        <v-select v-model="encoding" :items="encodings" label="Encoding" outlined />
      </v-col>
      <v-col v-if="precisaEncoding" cols="12" sm="4">
        <v-text-field v-model="delimitador" label="Delimitador" outlined maxlength="1" />
      </v-col>
      <v-col v-if="isZip" cols="12" :sm="precisaEncoding ? 4 : 6">
        <v-text-field
          v-model="arquivoInternoZip"
          label="Arquivo interno do ZIP"
          outlined
          hint="Obrigatório quando o upload for ZIP"
          persistent-hint
        />
      </v-col>
    </v-row>

    <v-row align="center" class="mx-auto mb-2">
      <v-col cols="12">
        <v-btn color="primary" :loading="uploading" :disabled="!arquivo || uploading || fakeFile" @click="upload">
          <v-icon left>mdi-cloud-upload</v-icon>
          Enviar arquivo
        </v-btn>
      </v-col>
    </v-row>

    <template v-if="campos.length">
      <v-divider class="my-6" />

      <div class="subtitle-1 font-weight-medium mb-3">Mapeamento de campos</div>

      <v-alert dense outlined type="info" color="blue" class="pa-2 mb-4">
        {{ colunas.length }} coluna(s) detectada(s)
        <span v-if="rowCount != null"> — {{ rowCount.toLocaleString('pt-BR') }} linha(s)</span>
        <span v-if="profilingStatus">
          — profiling:
          <b>{{ labelProfilingStatus }}</b>
        </span>
        . Arraste as linhas para definir a ordem do mapeamento. Clique no gráfico para ver as estatísticas de cada
        campo.
      </v-alert>

      <v-alert v-if="temMascaraAssimetrica" dense outlined type="warning" color="orange" class="pa-2 mb-4">
        Há máscara que altera o tipo de saída. Aplique a mesma variante no campo correspondente das duas bases.
      </v-alert>

      <v-simple-table dense class="mb-2 mapeamento-campos">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="campo-drag-col" />
              <th>Coluna origem</th>
              <th>Nome exibição</th>
              <th v-if="modoCorrespondencia">Correspondente</th>
              <th>Tipo</th>
              <th v-if="exigeQuantidade">Quantidade</th>
              <th>Máscara</th>
              <th class="text-center">Profiling</th>
            </tr>
          </thead>
          <draggable
            v-model="campos"
            tag="tbody"
            handle=".campo-drag-handle"
            v-bind="dragOptions"
            @end="sincronizarOrdem"
          >
            <tr v-for="(campo, idx) in campos" :key="campo.id || campo.colunaOrigem">
              <td class="campo-drag-col">
                <v-icon class="campo-drag-handle grey--text">mdi-drag-vertical</v-icon>
              </td>
              <td>{{ campo.colunaOrigem }}</td>
              <td>
                <v-text-field v-model="campo.nomeExibicao" dense outlined hide-details class="my-1" />
              </td>
              <td v-if="modoCorrespondencia">
                <v-select
                  v-model="campo.correspondente"
                  :items="opcoesCorrespondente(campo)"
                  dense
                  outlined
                  hide-details
                  class="my-1"
                  style="min-width: 180px"
                />
              </td>
              <td>
                <v-select
                  v-model="campo.tipoDado"
                  :items="tiposDado"
                  dense
                  outlined
                  hide-details
                  class="my-1"
                  style="min-width: 120px"
                  @change="onTipoDadoChange(campo)"
                />
              </td>
              <td v-if="exigeQuantidade">
                <v-checkbox
                  v-model="campo.isQuantidade"
                  dense
                  hide-details
                  class="mt-0"
                  @change="onQuantidadeChange(idx)"
                />
              </td>
              <td>
                <v-select
                  v-model="campo.nomeMascara"
                  :items="mascarasDoCampo(campo)"
                  dense
                  outlined
                  hide-details
                  clearable
                  class="my-1"
                  style="min-width: 160px"
                  @change="onMascaraChange(campo)"
                />
                <v-text-field
                  v-if="exigeQuantidadeDigitos(campo)"
                  v-model.number="campo.quantidadeDigitos"
                  type="number"
                  dense
                  outlined
                  hide-details
                  class="my-1"
                  :min="faixaQuantidadeDigitos.minimo"
                  :max="faixaQuantidadeDigitos.maximo"
                  :placeholder="rotuloQuantidadeDigitos"
                />
              </td>
              <td class="text-center">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="d-inline-block">
                      <v-btn
                        icon
                        small
                        :disabled="!campo.profiling"
                        :loading="profilingPendente && !campo.profiling"
                        @click="abrirProfiling(campo)"
                      >
                        <v-icon :color="campo.profiling ? 'primary' : undefined">mdi-chart-box-outline</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span>{{ campo.profiling ? 'Ver estatísticas do campo' : 'Profiling ainda não disponível' }}</span>
                </v-tooltip>
              </td>
            </tr>
          </draggable>
        </template>
      </v-simple-table>

      <v-row v-if="showSugestao" class="mt-2">
        <v-col cols="12">
          <v-btn outlined color="primary" :loading="loadingSugestao" @click="$emit('sugerir')">
            <v-icon left>mdi-auto-fix</v-icon>
            Aplicar sugestão de mapeamento
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <dialog-profiling-campo v-model="dialogProfiling" :campo="campoProfiling" />
  </div>
</template>

<script>
  import DialogProfilingCampo from '@/components/conciliacoesV3/DialogProfilingCampo.vue';
  import esquemaItensMixins from '@/mixins/conciliacoesV3/esquemaItensMixins';

  export default {
    name: 'MapeamentoBase',
    mixins: [esquemaItensMixins],
    components: {
      DialogProfilingCampo,
      draggable: () => import('vuedraggable')
    },
    props: {
      exigeQuantidade: { type: Boolean, default: false },
      showSugestao: { type: Boolean, default: false },
      uploading: { type: Boolean, default: false },
      loadingSugestao: { type: Boolean, default: false },
      initialColunas: { type: Array, default: () => [] },
      initialCampos: { type: Array, default: () => [] },
      initialProfilingStatus: { type: String, default: null },
      initialRowCount: { type: Number, default: null },
      initialArquivoOrigem: { type: String, default: null },
      initialNomeBase: { type: String, default: null },
      initialEncoding: { type: String, default: null },
      initialDelimitador: { type: String, default: null },
      initialArquivoInternoZip: { type: String, default: null },
      esquemas: { type: Object, default: () => ({}) },
      camposPartida: { type: Array, default: null }
    },
    data: () => ({
      arquivo: null,
      fakeFile: false,
      encoding: 'UTF-8',
      delimitador: ';',
      arquivoInternoZip: '',
      nomeBase: '',
      fileError: '',
      colunas: [],
      campos: [],
      profilingStatus: null,
      rowCount: null,
      dialogProfiling: false,
      campoProfiling: null,
      encodings: ['UTF-8', 'ISO-8859-1', 'Windows-1252']
    }),
    computed: {
      isZip() {
        const name = (this.arquivo && this.arquivo.name) || '';
        return /\.zip$/i.test(name);
      },
      precisaEncoding() {
        const name = (this.arquivo && this.arquivo.name) || '';
        return /\.(csv|tsv|txt)$/i.test(name) || this.isZip;
      },
      uploadSucesso() {
        return !!(this.arquivo && (this.colunas.length || this.campos.length) && !this.fileError);
      },
      nomeBaseTravado() {
        return this.fakeFile || this.uploadSucesso;
      },
      profilingPendente() {
        return String(this.profilingStatus || '').toUpperCase() === 'PENDENTE';
      },
      labelProfilingStatus() {
        const status = String(this.profilingStatus || '').toUpperCase();
        if (status === 'CONCLUIDO') return 'concluído';
        if (status === 'PENDENTE') return 'em andamento';
        if (status === 'ERRO') return 'erro';
        return this.profilingStatus;
      },
      parametroMascara() {
        return this.parametroDoEsquema(this.esquemas, 'MASCARA', 'mascara');
      },
      parametroQuantidadeDigitos() {
        return this.parametroDoEsquema(this.esquemas, 'MASCARA', 'quantidadeDigitos');
      },
      tiposDado() {
        const opcoes = this.opcoesDominio(this.parametroDoEsquema(this.esquemas, 'TRATAMENTO_POR_TIPO', 'tipoDado'));
        return opcoes.length ? opcoes.map((item) => item.value) : [];
      },
      faixaQuantidadeDigitos() {
        const parametro = this.parametroQuantidadeDigitos || {};
        return {
          minimo: parametro.minimo ?? parametro.min ?? 1,
          maximo: parametro.maximo ?? parametro.max ?? 255
        };
      },
      rotuloQuantidadeDigitos() {
        return this.parametroQuantidadeDigitos?.chave || 'quantidadeDigitos';
      },
      temMascaraAssimetrica() {
        return this.campos.some((campo) => {
          const variante = this.varianteMascara(this.parametroMascara, campo.nomeMascara);
          return !!(variante && variante.alteraTipoSaida);
        });
      },
      modoCorrespondencia() {
        return Array.isArray(this.camposPartida);
      },
      nomesPartida() {
        return (this.camposPartida || [])
          .filter((c) => c && c.nomeExibicao)
          .slice()
          .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
          .map((c) => c.nomeExibicao);
      },
      dragOptions() {
        return {
          animation: 200,
          ghostClass: 'campo-ghost'
        };
      }
    },
    watch: {
      initialColunas: {
        immediate: true,
        handler(val) {
          if (Array.isArray(val) && val.length) {
            this.colunas = val;
            if (!this.campos.length) {
              this.campos = this.buildCamposFromColunas(val);
            }
          }
        }
      },
      initialCampos: {
        immediate: true,
        handler(val) {
          if (Array.isArray(val) && val.length) {
            this.campos = this.ordenarCampos(val.map((c, i) => this.normalizarCampo(c, i)));
            this.hidratarCorrespondentes();
          }
        }
      },
      camposPartida() {
        this.hidratarCorrespondentes();
      },
      initialProfilingStatus: {
        immediate: true,
        handler(val) {
          this.profilingStatus = val;
        }
      },
      initialRowCount: {
        immediate: true,
        handler(val) {
          this.rowCount = val;
        }
      },
      initialArquivoOrigem: {
        immediate: true,
        handler() {
          this.hidratarArquivoSalvo();
        }
      },
      campos: {
        deep: true,
        handler() {
          this.$emit('mapping-change');
        }
      }
    },
    methods: {
      onFileChange() {
        if (this.fakeFile && this.arquivo && this.arquivo.size === 0) {
          return;
        }
        this.fakeFile = false;
        this.fileError = '';
        this.colunas = [];
        this.campos = [];
        this.profilingStatus = null;
        this.rowCount = null;
        this.nomeBase = this.nomeDoArquivoSemExtensao(this.arquivo && this.arquivo.name);
        if (this.isZip && !this.arquivoInternoZip) {
          this.fileError = 'Informe o arquivo interno do ZIP.';
        }
        this.$emit('mapping-change');
      },
      clearArquivo() {
        this.arquivo = null;
        this.fakeFile = false;
        this.arquivoInternoZip = '';
        this.nomeBase = '';
        this.fileError = '';
        this.colunas = [];
        this.campos = [];
        this.profilingStatus = null;
        this.rowCount = null;
        this.$emit('mapping-change');
      },
      hidratarArquivoSalvo() {
        const nome = this.initialArquivoOrigem;
        if (!nome) return;
        if (this.arquivo && !this.fakeFile) return;

        this.fakeFile = true;
        if (this.initialEncoding) this.encoding = this.initialEncoding;
        if (this.initialDelimitador) this.delimitador = this.initialDelimitador;
        this.arquivoInternoZip = this.initialArquivoInternoZip || '';
        this.nomeBase = this.initialNomeBase || this.nomeDoArquivoSemExtensao(nome);
        this.arquivo = new File([''], nome, { type: this.mimeDoArquivo(nome) });
      },
      nomeDoArquivoSemExtensao(nome) {
        return String(nome || '').replace(/\.[^./\\]+$/, '');
      },
      mimeDoArquivo(nome) {
        const n = String(nome || '').toLowerCase();
        if (n.endsWith('.csv')) return 'text/csv';
        if (n.endsWith('.tsv')) return 'text/tab-separated-values';
        if (n.endsWith('.txt')) return 'text/plain';
        if (n.endsWith('.zip')) return 'application/zip';
        if (n.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        if (n.endsWith('.xlsb')) return 'application/vnd.ms-excel.sheet.binary.macroEnabled.12';
        return 'application/octet-stream';
      },
      normalizarCampo(c, i) {
        const incidentes = c.itensN0Incidentes || c.itens_n0_incidentes || [];
        const mascaraN0 = incidentes.find((item) => String(item?.tipo || '').toUpperCase() === 'MASCARA');
        const parametrosN0 = mascaraN0?.parametros || {};
        return {
          id: c.id || null,
          colunaOrigem: c.colunaOrigem || c.coluna_origem,
          nomeExibicao: c.nomeExibicao || c.colunaOrigem || c.coluna_origem,
          tipoDado: c.tipoDado || c.tipoDetectado || this.tiposDado[0] || null,
          isQuantidade: !!c.isQuantidade,
          ordem: c.ordem != null ? c.ordem : i,
          nomeMascara: c.nomeMascara || parametrosN0.mascara || null,
          quantidadeDigitos: c.quantidadeDigitos ?? parametrosN0.quantidadeDigitos ?? null,
          amostra: c.amostra || [],
          profiling: c.profiling || null,
          itensN0Incidentes: incidentes,
          correspondente: null
        };
      },
      buildCamposFromColunas(colunas) {
        return colunas.map((col, i) => this.normalizarCampo(col, i));
      },
      mascarasDoCampo(campo) {
        const opcoes = this.opcoesPorTipoDado(this.parametroMascara, campo?.tipoDado).map((item) => ({
          text: item.text,
          value: item.value
        }));
        return [{ text: 'Nenhuma', value: null }, ...opcoes];
      },
      exigeQuantidadeDigitos(campo) {
        const variante = this.varianteMascara(this.parametroMascara, campo?.nomeMascara);
        return !!(variante && variante.exigeParametro === 'quantidadeDigitos');
      },
      onTipoDadoChange(campo) {
        const permitidas = this.mascarasDoCampo(campo).map((item) => item.value);
        if (campo.nomeMascara && !permitidas.includes(campo.nomeMascara)) {
          campo.nomeMascara = null;
          campo.quantidadeDigitos = null;
        }
      },
      onMascaraChange(campo) {
        if (!this.exigeQuantidadeDigitos(campo)) {
          campo.quantidadeDigitos = null;
        }
      },
      abrirProfiling(campo) {
        if (!campo?.profiling) return;
        this.campoProfiling = campo;
        this.dialogProfiling = true;
      },
      applyProfiling(payload) {
        this.profilingStatus = payload?.profilingStatus || this.profilingStatus;
        if (payload?.rowCount != null) this.rowCount = payload.rowCount;

        const byId = {};
        const byColuna = {};
        (payload?.campos || []).forEach((campo) => {
          if (!campo?.profiling) return;
          if (campo.id) byId[String(campo.id)] = campo.profiling;
          if (campo.colunaOrigem) byColuna[String(campo.colunaOrigem)] = campo.profiling;
        });

        this.campos.forEach((campo) => {
          const profiling = byId[String(campo.id || '')] || byColuna[String(campo.colunaOrigem || '')];
          if (profiling) campo.profiling = profiling;
        });
      },
      hidratarCorrespondentes() {
        if (!this.modoCorrespondencia) return;
        const nomes = new Set(this.nomesPartida);
        this.campos.forEach((campo) => {
          campo.correspondente = nomes.has(campo.nomeExibicao) ? campo.nomeExibicao : null;
        });
      },
      opcoesCorrespondente(campo) {
        const usados = new Set(
          this.campos.filter((outro) => outro !== campo && outro.correspondente).map((outro) => outro.correspondente)
        );
        return [
          { text: 'Nenhum', value: null },
          ...this.nomesPartida.map((nome) => ({ text: nome, value: nome, disabled: usados.has(nome) }))
        ];
      },
      aplicarSugestoes(sugestoes, confiancaMinima = 0.7) {
        let aplicadas = 0;
        (sugestoes || []).forEach((sugestao) => {
          if (!sugestao?.colunaReferencia || sugestao.confianca < confiancaMinima) return;
          const campo = this.campos.find((c) => c.nomeExibicao === sugestao.colunaReferencia);
          if (!campo) return;
          this.campos.forEach((outro) => {
            if (outro !== campo && outro.correspondente === sugestao.colunaPartida) outro.correspondente = null;
          });
          campo.correspondente = sugestao.colunaPartida;
          aplicadas++;
        });
        return aplicadas;
      },
      nomeGravado(campo) {
        if (this.modoCorrespondencia && campo.correspondente) return campo.correspondente;
        return campo.nomeExibicao || campo.colunaOrigem;
      },
      onQuantidadeChange(idx) {
        if (!this.exigeQuantidade) return;
        if (this.campos[idx].isQuantidade) {
          this.campos.forEach((c, i) => {
            if (i !== idx) c.isQuantidade = false;
          });
        }
      },
      ordenarCampos(campos) {
        return campos
          .slice()
          .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
          .map((campo, i) => ({ ...campo, ordem: i }));
      },
      sincronizarOrdem() {
        this.campos.forEach((campo, i) => {
          campo.ordem = i;
        });
      },
      upload() {
        this.fileError = '';
        if (!this.arquivo) {
          this.fileError = 'Selecione um arquivo.';
          return;
        }
        if (this.isZip && !this.arquivoInternoZip) {
          this.fileError = 'Informe o arquivo interno do ZIP.';
          return;
        }
        const formData = new FormData();
        formData.append('arquivo', this.arquivo);
        if (this.nomeBase.trim()) formData.append('nomeBase', this.nomeBase.trim());
        if (this.precisaEncoding && this.encoding) formData.append('encoding', this.encoding);
        if (this.precisaEncoding && this.delimitador) formData.append('delimitador', this.delimitador);
        if (this.isZip) formData.append('arquivoInternoZip', this.arquivoInternoZip);
        this.$emit('upload', formData);
      },
      applyUploadResult(upload) {
        this.fakeFile = false;
        this.colunas = upload?.colunas || [];
        this.profilingStatus = upload?.profilingStatus || null;
        if (upload?.nomeBase) this.nomeBase = upload.nomeBase;
        if (upload?.encoding) this.encoding = upload.encoding;
        if (upload?.delimitador) this.delimitador = upload.delimitador;

        const camposApi = Array.isArray(upload?.campos) ? upload.campos : [];
        if (camposApi.length) {
          const amostraPorColuna = {};
          (this.colunas || []).forEach((col) => {
            amostraPorColuna[col.colunaOrigem] = col.amostra || [];
          });
          this.campos = this.ordenarCampos(
            camposApi.map((c, i) => ({
              ...this.normalizarCampo(c, i),
              amostra: amostraPorColuna[c.colunaOrigem] || c.amostra || []
            }))
          );
        } else {
          this.campos = this.buildCamposFromColunas(this.colunas);
        }
        this.hidratarCorrespondentes();

        this.fileError = '';
        this.$emit('mapping-change');
      },
      validate() {
        if (!this.campos.length) return 'Envie o arquivo e configure o mapeamento.';
        if (this.campos.some((c) => !c.id)) {
          return 'Campos sem id. Envie o arquivo novamente.';
        }
        if (this.exigeQuantidade && !this.campos.some((c) => c.isQuantidade)) {
          return 'Marque um campo como quantidade.';
        }
        for (const c of this.campos) {
          if (this.exigeQuantidadeDigitos(c)) {
            const valor = Number(c.quantidadeDigitos);
            const { minimo, maximo } = this.faixaQuantidadeDigitos;
            if (!Number.isInteger(valor) || valor < minimo || valor > maximo) {
              return `Informe ${this.rotuloQuantidadeDigitos} entre ${minimo} e ${maximo} (${c.colunaOrigem}).`;
            }
          }
        }
        return true;
      },
      getPayload() {
        this.sincronizarOrdem();
        return {
          campos: this.campos.map((c, i) => {
            const payload = {
              id: c.id,
              colunaOrigem: c.colunaOrigem,
              nomeExibicao: this.nomeGravado(c),
              tipoDado: c.tipoDado,
              isQuantidade: !!c.isQuantidade,
              ordem: i,
              nomeMascara: c.nomeMascara || null
            };
            if (this.exigeQuantidadeDigitos(c) && c.quantidadeDigitos != null) {
              payload.quantidadeDigitos = Number(c.quantidadeDigitos);
            }
            return payload;
          })
        };
      }
    }
  };
</script>

<style scoped>
  .campo-drag-col {
    width: 36px;
    padding-left: 8px !important;
    padding-right: 0 !important;
  }

  .campo-drag-handle {
    cursor: grab;
  }

  .campo-drag-handle:active {
    cursor: grabbing;
  }

  .mapeamento-campos >>> .campo-ghost {
    opacity: 0.6;
    background: #f5f5f5;
  }
</style>
